import '../styles/app.scss';

import React, { useEffect } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';

import { useGetClientConfigQuery } from '../datasource/queries/clientConfig';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useAuthCheck } from '../hooks/useAuthCheck';
import { datadogRum, initDatadog } from '../hooks/useDatadog';
import { useEnhancedRouteTracker } from '../hooks/useEnhancedRouteTracker';
import { useEventHubDataLayerConsumer } from '../hooks/useEventHubDataLayerConsumer';
import { DeploymentEnvironmentBanner } from './DeploymentEnvironmentBanner/DeploymentEnvironmentBanner';
import Home from './Home/Home';
import Loader from './Loader/Loader';
import { MainError } from './MainError/MainError';
import { Redirect } from './Redirect/Redirect';
import { RequestCreditsPage } from './RequestCreditsPage/RequestCreditsPage';
import { setClientConfig } from './Store/clientConfigSlice';
import { setUser } from './Store/userSlice';
import { SurveyPage } from './SurveyPage/SurveyPage';

export interface IAppProps {
    identity: IIdentity;
    /**
     * Deployment environment label returned by Watch configuration, such as BLUE or GREEN.
     * When omitted or empty, the deployment environment banner is hidden.
     */
    deploymentEnvironment?: string;
    isDevelopment?: boolean;
}
export interface IIdentity {
    name: string;
    email: string;
    id: string;
    individualSK: number;
    isAuthorized: boolean;
    isPliEmployee: boolean;
    isGroupcast: boolean;
    isIndividual: boolean;
    itemSK: number;
    organizationSK: number;
    isPM: boolean;
    isIM: boolean;
    hasAiAccess: boolean;

    /**
     * The server could not determine authorization (its own downstream identity lookup failed).
     * This is not a signed-out answer — it means "we don't know" — so bootstrap retries the
     * identity fetch a few times to give a transient blip a chance to clear (see
     * fetchIdentityWithRetry in index.tsx) before isLoggedIn ever sees it. If it is still
     * indeterminate here, retries are exhausted: isLoggedIn falls back to fail-closed rather than
     * granting a never-authenticated visitor access during a prolonged outage. An already
     * signed-in viewer hitting this same case mid-session is unaffected — useAuthCheck's periodic
     * re-check treats indeterminate as "keep the session as-is" and never reloads on it.
     */
    isIndeterminate?: boolean;
}

export function isLoggedIn(identity: IIdentity) {
    return identity.isAuthorized;
}

export function isPliDomainUser(identity: IIdentity) {
    const email = identity.email?.trim().toLowerCase() ?? '';

    return email.endsWith('@pli.edu');
}


function App(props: IAppProps) {
    useAuthCheck();
    const { processEvents } = useEventHubDataLayerConsumer();
    useEnhancedRouteTracker();
    const dispatch = useAppDispatch();
    const individualSk = props.identity.individualSK.toString();

    // Check if there's a global error that should hide all app content
    const globalError = useAppSelector((state) => state.errorBoundary?.hasGlobalError);
    const errorProps = useAppSelector((state) => state.errorBoundary?.errorProps);

    const { data: configData, isFetching: isConfigFetching, isLoading: isConfigLoading, isError: isConfigError } = useGetClientConfigQuery();


    useEffect(() => {
        if (!isConfigFetching && configData && !isConfigLoading && !isConfigError) {
            dispatch(setClientConfig(configData));
            initDatadog(configData.datadog);
        }
    }, [configData, isConfigFetching, dispatch, isConfigLoading, isConfigError]);

    useEffect(() => {
        dispatch(setUser(props.identity));
        if (individualSk) {
            datadogRum.setUser({ id: individualSk });
        }
        else {
            (datadogRum as any).removeUser();
        }
    }, [dispatch, individualSk, props.identity]);

    useEffect(() => {
        window.onbeforeunload = () => {
            processEvents();
        };
    }, [processEvents]);

    useEffect(() => {
        document.title = 'Practising Law Institute CLE Now';
    }, []);

    const deploymentEnvironmentBanner = (
        <DeploymentEnvironmentBanner
            environment={props.deploymentEnvironment}
            isVisible={props.identity.isPliEmployee || isPliDomainUser(props.identity) || props.isDevelopment === true}
        />
    );

    // If global error is set, render only the error page
    if (globalError && errorProps) {
        return (
            <>
                {deploymentEnvironmentBanner}
                <div
                    className="d-flex justify-content-center align-items-center w-100"
                    style={{ minHeight: '100vh' }}
                >
                    <MainError {...errorProps} />
                </div>
            </>
        );
    }

    return (
        <>
            {deploymentEnvironmentBanner}
            <Routes>
                <Route path='/' element={!isLoggedIn(props.identity) ? (<Redirect url="/login" />) : <Outlet />}>
                    <Route index element={<Home />} />
                    <Route path='launching' element={<Loader />} />
                    <Route path='mcle' element={<RequestCreditsPage />} />
                    <Route path='survey' element={<SurveyPage />} />
                    <Route path='*' element={<Redirect url="/" />} />
                </Route>
            </Routes>
        </>
    );
}

export default App;
